/**
 * SupervisorDetailDialog — popup that shows a single supervisor's full record
 * with attachments (view/download/delete + add new attachment).
 *
 * iter-218f (Feb 2026) — Job title is now editable directly from this dialog
 * via a dropdown that pulls from `/api/dropdowns/job-titles`. The edit
 * capability was intentionally REMOVED from the "Supervisors & Staff
 * Accounts" page and consolidated here.
 *
 * Props:
 *  - open, onOpenChange
 *  - supervisorId
 *  - language
 *  - onChanged (called after any add/delete so parent can refresh)
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Mail, Phone, User, IdCard, Briefcase, Calendar, Globe2,
  Paperclip, Upload, FileImage, FileText, Trash2, Eye, Loader2, Download,
  Pencil, Check, Save,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { getAuthHeaders } from '../../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_OPTIONS = [
  { value: 'id_front', label_ar: 'صورة الهوية (وجه أمامي)', label_en: 'Emirates ID — Front' },
  { value: 'id_back',  label_ar: 'صورة الهوية (وجه خلفي)', label_en: 'Emirates ID — Back' },
  { value: 'visa',     label_ar: 'إقامة',                    label_en: 'Visa / Residence' },
  { value: 'passport', label_ar: 'جواز السفر',                label_en: 'Passport' },
  { value: 'photo',    label_ar: 'صورة شخصية',                label_en: 'Personal photo' },
  { value: 'contract', label_ar: 'عقد',                       label_en: 'Contract' },
  { value: 'other',    label_ar: 'مستند آخر',                 label_en: 'Other document' },
];

function kindLabel(kind, language) {
  const opt = KIND_OPTIONS.find((k) => k.value === kind);
  if (!opt) return language === 'ar' ? 'مستند' : 'Document';
  return language === 'ar' ? opt.label_ar : opt.label_en;
}

function getAuthHeader() {
  return getAuthHeaders();
}

function AttachmentTile({ att, language, onView, onDelete, deleting }) {
  const isImage = (att.content_type || '').startsWith('image/');
  return (
    <div className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl p-3 flex flex-col gap-2" data-testid={`attachment-${att.file_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isImage
            ? <FileImage className="h-4 w-4 text-[#d4af37] shrink-0" />
            : <FileText className="h-4 w-4 text-blue-400 shrink-0" />}
          <div className="min-w-0">
            <p className="text-xs text-[#d4af37] font-semibold truncate">{kindLabel(att.kind, language)}</p>
            <p className="text-[10px] text-[#606060] truncate" dir="ltr">{att.filename || att.label}</p>
          </div>
        </div>
        <span className="text-[10px] text-[#606060] shrink-0">
          {att.size ? `${(att.size / 1024).toFixed(0)}KB` : ''}
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onView(att)}
          className="flex-1 h-7 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          data-testid={`view-attachment-${att.file_id}`}
        >
          <Eye className="h-3 w-3 me-1" />
          {language === 'ar' ? 'عرض' : 'View'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDelete(att)}
          disabled={deleting === att.file_id}
          className="h-7 px-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
          data-testid={`delete-attachment-${att.file_id}`}
        >
          {deleting === att.file_id
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <Trash2 className="h-3 w-3" />}
        </Button>
      </div>
    </div>
  );
}

export default function SupervisorDetailDialog({
  open,
  onOpenChange,
  supervisorId,
  language,
  onChanged,
}) {
  const [loading, setLoading] = useState(false);
  const [sup, setSup] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedKind, setSelectedKind] = useState('other');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [viewer, setViewer] = useState(null); // { src, content_type, filename }
  const [viewerLoading, setViewerLoading] = useState(false);
  // iter-218f (Feb 2026) — Job title editing (dropdown fed by /dropdowns/job-titles).
  const [jobTitles, setJobTitles] = useState([]);
  const [jobTitlesLoading, setJobTitlesLoading] = useState(false);
  const [jobEditing, setJobEditing] = useState(false);
  const [jobSelectedId, setJobSelectedId] = useState('');
  const [jobSaving, setJobSaving] = useState(false);

  const fetchSupervisor = useCallback(async () => {
    if (!supervisorId) return;
    setLoading(true);
    setViewer(null);
    setSelectedFile(null);
    setSelectedKind('other');
    setSelectedLabel('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/supervisor/${supervisorId}`, { headers: getAuthHeader() });
      setSup(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل تحميل السجل' : 'Failed to load record'));
    } finally {
      setLoading(false);
    }
  }, [supervisorId, language]);

  useEffect(() => {
    if (open && supervisorId) {
      fetchSupervisor();
    }
  }, [open, supervisorId, fetchSupervisor]);

  // iter-218f — Preload the job-titles dropdown once the dialog opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      setJobTitlesLoading(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/dropdowns/job-titles`,
          { headers: getAuthHeader(), params: { active_only: true } },
        );
        setJobTitles(res.data?.data || []);
      } catch {
        /* silent — the editor just shows an empty list */
      } finally {
        setJobTitlesLoading(false);
      }
    })();
  }, [open]);

  const startEditJob = () => {
    setJobSelectedId(sup?.job_title_id || '');
    setJobEditing(true);
  };
  const cancelEditJob = () => {
    setJobEditing(false);
    setJobSelectedId('');
  };
  const saveJob = async () => {
    const picked = jobTitles.find((j) => (j.id || j._id) === jobSelectedId);
    if (!picked) {
      toast.error(language === 'ar' ? 'اختر مسمى وظيفياً' : 'Select a job title');
      return;
    }
    setJobSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/supervisor/${supervisorId}`,
        {
          job_title_id: picked.id || picked._id,
          job_title_ar: picked.name_ar,
          // iter-218s (Feb 2026) — Send BOTH `job_title` and `job_title_en`
          // so the backend's cascade to users + admin_employee_signatures
          // and the employee-lookup fallbacks all see the EN value. The
          // legacy `job_title` alias is kept for old readers that still
          // expect it.
          job_title:    picked.name_en,
          job_title_en: picked.name_en,
        },
        { headers: getAuthHeader() },
      );
      toast.success(language === 'ar' ? 'تم تحديث المسمى الوظيفي' : 'Job title updated');
      setJobEditing(false);
      await fetchSupervisor();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setJobSaving(false);
    }
  };

  const handleView = async (att) => {
    setViewerLoading(true);
    setViewer({ filename: att.filename, content_type: att.content_type, src: null });
    try {
      const res = await axios.get(`${BACKEND_URL}/api/upload/file/${att.file_id}?raw=false`, { headers: getAuthHeader() });
      const dataUrl = `data:${res.data.content_type || 'application/octet-stream'};base64,${res.data.data}`;
      setViewer({ filename: res.data.filename, content_type: res.data.content_type, src: dataUrl });
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل عرض الملف' : 'Failed to load file');
      setViewer(null);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(language === 'ar' ? 'حذف هذا المرفق نهائياً؟' : 'Delete this attachment?')) return;
    setDeleting(att.file_id);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/supervisor/${supervisorId}/attachments/${att.file_id}`,
        { headers: getAuthHeader() },
      );
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
      await fetchSupervisor();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setDeleting(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(language === 'ar' ? 'اختر ملفاً أولاً' : 'Pick a file first');
      return;
    }
    setUploading(true);
    try {
      // 1) upload raw file
      const fd = new FormData();
      fd.append('file', selectedFile, selectedFile.name);
      const upRes = await axios.post(`${BACKEND_URL}/api/upload/file`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const fileId = upRes.data?.file_id;
      if (!fileId) throw new Error('upload returned no file_id');

      // 2) attach to supervisor
      await axios.post(`${BACKEND_URL}/api/admin/supervisor/${supervisorId}/attachments`, {
        file_id: fileId,
        kind: selectedKind,
        label: selectedLabel || selectedFile.name,
      }, { headers: getAuthHeader() });

      toast.success(language === 'ar' ? 'تم إضافة المرفق' : 'Attachment added');
      setSelectedFile(null);
      setSelectedLabel('');
      setSelectedKind('other');
      await fetchSupervisor();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل الرفع' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  // Personal photo: first attachment with kind=photo, used as avatar
  const personalPhoto = (sup?.attachments || []).find((a) => a.kind === 'photo');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0f0f1e] border-[#2a2a4e] text-white max-w-3xl max-h-[90vh] overflow-y-auto"
        dir="rtl"
        data-testid="supervisor-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <User className="h-5 w-5 text-amber-400" />
            {language === 'ar' ? 'تفاصيل الموظف' : 'Employee Details'}
          </DialogTitle>
        </DialogHeader>

        {loading || !sup ? (
          <div className="py-12 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-amber-400 mx-auto" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* ===== Profile Header ===== */}
            <div className="flex items-center gap-4 p-4 bg-[#1a1a2e] rounded-xl border border-[#2a2a4e]">
              {personalPhoto ? (
                <button
                  onClick={() => handleView(personalPhoto)}
                  className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-400/40 hover:border-amber-400 transition-colors"
                  data-testid="profile-photo-btn"
                >
                  <div className="w-full h-full bg-amber-500/10 flex items-center justify-center">
                    <FileImage className="h-7 w-7 text-amber-400" />
                  </div>
                </button>
              ) : (
                <div className="w-16 h-16 rounded-full bg-amber-500/15 border-2 border-amber-400/30 flex items-center justify-center">
                  <User className="h-8 w-8 text-amber-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-white truncate">
                  {sup.full_name_ar || `${sup.first_name || ''} ${sup.last_name || ''}`.trim() || (language === 'ar' ? 'بدون اسم' : 'No name')}
                </p>
                {sup.full_name_en && <p className="text-xs text-[#A1A1AA] truncate">{sup.full_name_en}</p>}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {sup.employee_id && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37]">
                      ID: {sup.employee_id}
                    </span>
                  )}
                  {sup.has_account ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                      {language === 'ar' ? 'له حساب يوزر' : 'Has user account'}
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                      {language === 'ar' ? 'بدون حساب' : 'No account'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* ===== Personal data grid ===== */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="personal-data-grid">
              <DataRow icon={Mail}     label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}   value={sup.email} dir="ltr" />
              <DataRow icon={Phone}    label={language === 'ar' ? 'الهاتف' : 'Phone'}              value={sup.phone} dir="ltr" />
              <DataRow icon={IdCard}   label={language === 'ar' ? 'رقم الهوية' : 'EID number'}     value={sup.id_number} dir="ltr" />
              {/* iter-218f — Job title is now editable in-place. */}
              <JobTitleEditorRow
                language={language}
                supervisor={sup}
                jobTitles={jobTitles}
                jobTitlesLoading={jobTitlesLoading}
                editing={jobEditing}
                selectedId={jobSelectedId}
                setSelectedId={setJobSelectedId}
                saving={jobSaving}
                onStartEdit={startEditJob}
                onCancel={cancelEditJob}
                onSave={saveJob}
              />
              <DataRow icon={Globe2}   label={language === 'ar' ? 'الجنسية' : 'Nationality'}        value={
                language === 'ar'
                  ? (sup.nationality_ar || sup.nationality || '—')
                  : (sup.nationality_en || sup.nationality || '—')
              } />
              <DataRow icon={Calendar} label={language === 'ar' ? 'تاريخ الميلاد' : 'DOB'}          value={sup.date_of_birth} dir="ltr" />
              <DataRow icon={Calendar} label={language === 'ar' ? 'انتهاء الهوية' : 'EID expiry'}   value={sup.expiry_date} dir="ltr" />
              <DataRow icon={Calendar} label={language === 'ar' ? 'تاريخ الإدراج' : 'Registered'}   value={sup.created_at ? new Date(sup.created_at).toLocaleString() : ''} />
            </div>

            {/* ===== Attachments ===== */}
            <div className="border-t border-white/10 pt-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
                <Paperclip className="h-4 w-4 text-amber-400" />
                {language === 'ar' ? 'المرفقات' : 'Attachments'}
                <span className="text-[#71717A] text-xs font-normal">({(sup.attachments || []).length})</span>
              </h3>

              {(sup.attachments || []).length === 0 ? (
                <p className="text-xs text-[#606060] py-3 text-center">
                  {language === 'ar' ? 'لا توجد مرفقات بعد' : 'No attachments yet'}
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {(sup.attachments || []).map((att) => (
                    <AttachmentTile
                      key={att.file_id}
                      att={att}
                      language={language}
                      onView={handleView}
                      onDelete={handleDelete}
                      deleting={deleting}
                    />
                  ))}
                </div>
              )}

              {/* ===== Add new attachment ===== */}
              <div className="mt-4 p-3 bg-[#1a1a2e] border border-[#2a2a4e] rounded-xl space-y-3" data-testid="add-attachment-form">
                <p className="text-xs text-amber-300 font-semibold flex items-center gap-1">
                  <Upload className="h-3.5 w-3.5" />
                  {language === 'ar' ? 'إضافة مرفق جديد' : 'Add new attachment'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    value={selectedKind}
                    onChange={(e) => setSelectedKind(e.target.value)}
                    className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                    data-testid="attachment-kind-select"
                  >
                    {KIND_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {language === 'ar' ? opt.label_ar : opt.label_en}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={selectedLabel}
                    onChange={(e) => setSelectedLabel(e.target.value)}
                    placeholder={language === 'ar' ? 'وصف اختياري' : 'Optional label'}
                    className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                    data-testid="attachment-label-input"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <label
                    htmlFor="att-file-input"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-dashed border-amber-500/40 rounded-lg cursor-pointer hover:bg-amber-500/5 text-xs text-amber-300"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    {selectedFile ? selectedFile.name : (language === 'ar' ? 'اختر ملفاً' : 'Pick a file')}
                  </label>
                  <input
                    id="att-file-input"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="attachment-file-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="attachment-upload-btn"
                  >
                    {uploading
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Upload className="h-3.5 w-3.5 me-1" />}
                    {language === 'ar' ? 'رفع' : 'Upload'}
                  </Button>
                </div>
              </div>
            </div>

            {/* ===== Inline viewer ===== */}
            {viewer && (
              <div className="border-t border-white/10 pt-4" data-testid="attachment-viewer">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-amber-300 font-semibold">
                    {viewer.filename || (language === 'ar' ? 'عرض المرفق' : 'Attachment preview')}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {viewer.src && (
                      <a
                        href={viewer.src}
                        download={viewer.filename}
                        className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                        data-testid="download-attachment-link"
                      >
                        <Download className="h-3.5 w-3.5" />
                        {language === 'ar' ? 'تحميل' : 'Download'}
                      </a>
                    )}
                    <button onClick={() => setViewer(null)} className="text-xs text-[#808080] hover:text-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl p-2 max-h-[400px] overflow-auto flex items-center justify-center">
                  {viewerLoading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-amber-400 my-6" />
                  ) : viewer.src && (viewer.content_type || '').startsWith('image/') ? (
                    <img src={viewer.src} alt={viewer.filename} className="max-w-full max-h-[380px] object-contain" />
                  ) : viewer.src && (viewer.content_type || '').includes('pdf') ? (
                    <iframe src={viewer.src} title={viewer.filename} className="w-full h-[380px] bg-white rounded" />
                  ) : (
                    <p className="text-xs text-[#808080] p-4">
                      {language === 'ar' ? 'تعذّر عرض هذا النوع — استخدم زر التحميل' : 'Cannot preview — use download'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DataRow({ icon: Icon, label, value, dir }) {
  if (!value) return null;
  return (
    <div className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-[#71717A] shrink-0" />
      <span className="text-[10px] text-[#606060] shrink-0">{label}:</span>
      <span className="text-xs text-white truncate" dir={dir}>{value}</span>
    </div>
  );
}

// iter-218f (Feb 2026) — Inline job-title editor. Renders a read-only pill
// with a pencil button; on click swaps to a dropdown (fed by /dropdowns/
// job-titles) + save/cancel buttons.
function JobTitleEditorRow({
  language, supervisor, jobTitles, jobTitlesLoading,
  editing, selectedId, setSelectedId, saving,
  onStartEdit, onCancel, onSave,
}) {
  const label = language === 'ar' ? 'المسمى الوظيفي' : 'Job title';
  const current = supervisor?.job_title_ar || supervisor?.job_title || '';

  if (!editing) {
    return (
      <div className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-lg px-3 py-2 flex items-center gap-2"
           data-testid="supervisor-job-title-row">
        <Briefcase className="h-3.5 w-3.5 text-[#71717A] shrink-0" />
        <span className="text-[10px] text-[#606060] shrink-0">{label}:</span>
        <span className="text-xs text-white truncate flex-1"
              data-testid="supervisor-job-title-value">
          {current || (language === 'ar' ? '— لم يُحدَّد —' : '— not set —')}
        </span>
        <button
          type="button"
          onClick={onStartEdit}
          className="h-6 w-6 rounded-md flex items-center justify-center text-[#71717A] hover:text-[#d4af37] hover:bg-[#d4af37]/10 transition shrink-0"
          title={language === 'ar' ? 'تعديل المسمى الوظيفي' : 'Edit job title'}
          data-testid="supervisor-job-title-edit-btn"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] border border-[#d4af37]/40 rounded-lg px-3 py-2 flex items-center gap-2"
         data-testid="supervisor-job-title-editor">
      <Briefcase className="h-3.5 w-3.5 text-[#d4af37] shrink-0" />
      <select
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        className="flex-1 min-w-0 h-7 rounded-md bg-[#111] border border-[#2a2a4e] text-white text-xs px-2 focus:outline-none focus:border-[#d4af37]/60 [color-scheme:dark]"
        data-testid="supervisor-job-title-select"
      >
        <option value="" style={{ backgroundColor: '#111', color: '#fff' }}>
          {jobTitlesLoading
            ? (language === 'ar' ? 'جارٍ التحميل…' : 'Loading…')
            : (language === 'ar' ? '— اختر المسمى —' : '— Select title —')}
        </option>
        {jobTitles.map((t) => (
          <option key={t.id || t._id} value={t.id || t._id} style={{ backgroundColor: '#111', color: '#fff' }}>
            {language === 'ar' ? t.name_ar : (t.name_en || t.name_ar)}
          </option>
        ))}
      </select>
      <button
        onClick={onSave}
        disabled={saving || !selectedId}
        className="h-7 w-7 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/25 disabled:opacity-50 shrink-0"
        title={language === 'ar' ? 'حفظ' : 'Save'}
        data-testid="supervisor-job-title-save-btn"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </button>
      <button
        onClick={onCancel}
        className="h-7 w-7 rounded-md bg-white/5 border border-white/10 text-[#808080] flex items-center justify-center hover:bg-white/10 shrink-0"
        title={language === 'ar' ? 'إلغاء' : 'Cancel'}
        data-testid="supervisor-job-title-cancel-btn"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
